import React, { useCallback, useEffect, useState } from 'react'

import KeyboardAvoidingScrollView from '@zet/common/components/KeyboardAvoidingScrollView'
import { FormProvider, useForm } from 'react-hook-form'

import { useApiAction } from '../../api/useApiAction'
import { convertFormDataToPayload } from '../../utils/convertFormDataToPayload'
import { setFormData } from '../../utils/setFormValues'
import { IOcField } from '../../utils/types'
import bottomSheetComponentMap from '../common/bottomSheets'
import Footer from '../footer/footer'
import Form from './form'
import { ajvResolver } from './resolver'
import useFormStore from './store'

export const FormBuilder = (_props: any) => {
  const { state, props, userDetails } = _props

  const { data, error, isLoading, stateManagePost } = useApiAction()

  const modalRef = React.useRef<any>(null)

  const ocFields = data?.layout?.ocFields
  const fieldValues = data?.layout?.fieldValues
  const step = data?.layout?.step || data?.metadata?.step
  const title = data?.layout?.title
  const arnNumber = data?.metadata?.arnNumber
  const { params, setParams } = useFormStore((state) => state)

  const subTitle = data?.layout?.description?.replace(
    /{(.*?)}/g,
    (_: string, key: string) => data?.metadata[key] || `{${key}}`
  )
  const ocFieldErrors = data?.error?.errorFieldIds ?? error?.formErrors
  const validationSchema = data?.layout?.validationSchema || {}
  const consents = props?.consents
  const disableConsents = props?.disableConsents
  const [userConsents, setUserConsents] = useState(
    Array(consents?.length).fill(!disableConsents)
  )
  const prevState = data?.prevState || ''

  const { control, handleSubmit, setError, setValue, ...methods } = useForm({
    resolver: ajvResolver(validationSchema),
    mode: 'onTouched',
  })

  const onSubmit = (formData: any) => {
    let payloadEvent: string | null | undefined = null
    if (props?.shareEvent) {
      if (formData) {
        const keys = Object.entries(formData)
        if (keys.length) {
          const eventKey = keys[0][0] ?? ''
          const field: IOcField = data?.layout?.ocFields?.find(
            (element: IOcField) => element?.id === eventKey
          )
          const eventValue = field?.fieldOptions?.find(
            (element: { optionId: string; optionValue: string }) =>
              element.optionId === keys[0][1]
          )
          payloadEvent = eventValue?.optionId ?? null
        }
      }
    } else {
      payloadEvent = params?.event
      setParams({})
    }

    let apiData: any = {
      payload: convertFormDataToPayload(formData, consents),
    }

    if (modalRef?.current?.isVisible()) {
      modalRef?.current?.closeModal()
      apiData = {
        ...apiData,
        payload: {
          popupContinued: true,
          ...apiData?.payload,
        },
      }
    }
    apiData = {
      ...apiData,
      extras: {
        event: payloadEvent,
      },
      options: {
        onSuccess: (data: any) => {
          if (data?.data?.data?.popup) {
            modalRef?.current?.openModal()
          }
        },
      },
    }

    stateManagePost(apiData)
  }

  const setFormErrors = () => {
    ocFieldErrors?.forEach?.((error) => {
      if (error?.code) {
        setError(String(error.code), {
          type: 'server',
          message: error?.message ?? 'This field is invalid',
        })
      } else {
        setError(error, {
          type: 'server',
          message: 'This field is invalid',
        })
      }
    })
  }

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    if (ocFields?.length) {
      // setFormData(ocFields, setValue)
      setFormData(fieldValues, setValue)
      if (ocFieldErrors) {
        timeout = setTimeout(() => {
          setFormErrors()
        })
      }
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [fieldValues])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    if (ocFieldErrors && !isLoading) {
      timeout = setTimeout(() => {
        setFormErrors()
      })
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [ocFieldErrors, isLoading])

  const onModalButtonPress = () => {
    handleSubmit(onSubmit)()
  }

  const SheetComponent = useCallback(() => {
    if (data) {
      const modal = bottomSheetComponentMap[data?.popup]
      if (!modal) {
        return <></>
      }
      const Component = modal?.component

      return (
        <Component
          ref={modalRef}
          onContinuePress={onModalButtonPress}
          isLoading={isLoading}
        />
      )
    }
    return <></>
  }, [data, isLoading])

  // useEffect(() => {
  //   if (formSubmitError) {
  //     // toast?.show({
  //     //   intent: 'error',
  //     //   heading: brandsI18n.t('toastMessages.error'),
  //     //   description: formSubmitError?.message,
  //     //   hideAfter: 3000,
  //     // })
  //   }
  // }, [formSubmitError])

  return (
    <KeyboardAvoidingScrollView contentContainerClassName="bg-white">
      <FormProvider
        control={control}
        handleSubmit={handleSubmit}
        setError={setError}
        setValue={setValue}
        {...methods}
      >
        <Form
          control={control}
          ocFields={ocFields}
          title={title}
          subTitle={subTitle}
          arnNumber={props?.showArnNumber ? arnNumber : ''}
          userDetails={userDetails}
          state={state}
          step={step}
          prevState={prevState}
          {...props}
        />
      </FormProvider>
      <Footer
        isForm
        control={control}
        buttonProps={{
          ...props?.buttonProps,
          loading: isLoading,
          onPress: handleSubmit(onSubmit),
          disabled: !userConsents?.every((element) => element === true),
        }}
        disabled={true}
        consents={consents}
        userConsents={userConsents}
        setUserConsents={setUserConsents}
        step={step}
      />
      {SheetComponent && SheetComponent()}
    </KeyboardAvoidingScrollView>
  )
}
